import * as Sentry from '@sentry/react'
import { browserTracingIntegration, extraErrorDataIntegration } from '@sentry/react'
import { isExpectedError } from './error-reporting'
import { apiTraceTargets, scrubSentryEvent } from './observability'

/**
 * Initialize Sentry error monitoring, session replay, and tracing
 *
 * Call this ONCE at app startup, before any other code runs.
 */
export function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN
  const environment = import.meta.env.VITE_SENTRY_ENVIRONMENT || 'development'
  const release = __SENTRY_RELEASE__ || undefined
  const debug = import.meta.env.VITE_SENTRY_DEBUG === 'true'

  // Don't initialize if no DSN is provided
  if (!dsn) {
    console.log('Sentry DSN not configured - error monitoring disabled')
    return
  }

  // Capture complete traces by default. Production can still tune this with
  // VITE_SENTRY_TRACES_SAMPLE_RATE if volume/cost requires it.
  const tracesSampleRate = parseFloat(
    import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE || '1.0'
  )

  Sentry.init({
    dsn,
    environment,
    release,
    debug,
    initialScope: { tags: { app: 'frontend' } },

    // Performance monitoring
    tracesSampleRate,

    // Trace API calls to backend (Sentry v8 moves this to top-level)
    tracePropagationTargets: apiTraceTargets(import.meta.env.VITE_API_URL),

    // Session Replay is error-only: no unprompted session recordings.
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors

    // Integrations
    integrations: [
      // Browser tracing for performance monitoring
      browserTracingIntegration(),

      // Session replay with privacy controls
      Sentry.replayIntegration({
        maskAllText: false,
        maskAllInputs: true, // Mask all input fields for privacy
        blockAllMedia: false,
        networkDetailAllowUrls: apiTraceTargets(import.meta.env.VITE_API_URL),
        networkCaptureBodies: false,
        networkRequestHeaders: ['X-Request-ID'],
        networkResponseHeaders: ['X-Request-ID', 'X-Trace-ID'],
      }),

      // Serialize own-properties of non-Error rejections so future events
      // carry context instead of just "Object captured as promise rejection"
      extraErrorDataIntegration({ depth: 5 }),
    ],

    // Filter out common noise
    ignoreErrors: [
      // Browser extension errors
      'top.GLOBALS',
      'originalCreateNotification',
      'canvas.contentDocument',
      'MyApp_RemoveAllHighlights',
      'atomicFindClose',
      'fb_xd_fragment',
      'bmi_SafeAddOnload',
      'EBCallBackMessageReceived',
      'conduitPage',
      // Stale JS chunks after deployment — Vercel SPA rewrite serves HTML
      // for missing hashed assets, and the browser rejects the MIME type.
      // A page refresh resolves it. (Sentry CORTADO-PORTAL-9)
      "is not a valid JavaScript MIME type",
      // AbortErrors from TanStack Query cancelling in-flight requests
      // during query invalidation (e.g., auth state changes, navigation)
      'AbortError',
      'The user aborted a request',
      'signal is aborted without reason',
      'Fetch is aborted',
      'The operation was aborted',
      // Network-level failures from the browser fetch — user offline, tunnel,
      // CORS preflight drop, OS sleep mid-request. Not actionable on our side.
      // (Sentry CORTADO-FRONTEND-1A/1E/etc.)
      'Failed to fetch',
      'NetworkError when attempting to fetch resource',
      'Load failed',
      // CodeUnauthenticated is expected during logout
      'CodeUnauthenticated',
      'code = Unauthenticated',
      // Empty-object promise rejections from background 3rd-party promises
      // (Safari ITP edge cases in Supabase autoRefresh, browser instrumentation, etc.)
      // — no stack, no message, not actionable. (Sentry CORTADO-FRONTEND-W)
      'Object captured as promise rejection with keys: [object has no keys]',
    ],

    // Filter events before sending
    beforeSend(event, _hint) {
      // In development, only send events if VITE_SENTRY_DEBUG is explicitly set
      if (environment === 'development' && !debug) {
        console.log('Sentry event blocked in development (set VITE_SENTRY_DEBUG=true to send):', event)
        return null
      }

      // Filter out AbortErrors (TanStack Query cancels in-flight requests
      // during invalidation — these are expected, not bugs)
      const exceptionType = event.exception?.values?.[0]?.type
      if (exceptionType === 'AbortError') {
        return null
      }

      // Filter out browser extension errors
      if (event.exception?.values?.[0]?.stacktrace?.frames) {
        const frames = event.exception.values[0].stacktrace.frames
        if (frames.some((frame) => frame.filename?.includes('extension://'))) {
          return null
        }
      }

      return scrubSentryEvent(event)
    },
    beforeSendTransaction: scrubSentryEvent,
    beforeBreadcrumb: scrubSentryEvent,
  })

  console.log(`Sentry initialized (${environment})`)
}

/**
 * Set the current user context in Sentry
 *
 * Call this after successful login to associate errors with specific users.
 */
export function setSentryUser(user: {
  id: string
  email?: string
  orgId?: string
}) {
  Sentry.setUser({
    id: user.id,
    email: user.email,
    organization_id: user.orgId,
  })
}

/**
 * Clear the user context in Sentry
 *
 * Call this on logout to stop associating errors with the previous user.
 */
export function clearSentryUser() {
  Sentry.setUser(null)
}

/**
 * Manually capture an error with additional context
 *
 * @example
 * try {
 *   await riskyOperation()
 * } catch (error) {
 *   captureError(error, {
 *     operation: 'riskyOperation',
 *     userId: user.id,
 *   })
 * }
 */
export function captureError(error: Error, context?: Record<string, unknown>) {
  // Expected, user-actionable errors are handled outcomes, not bugs — every
  // capture path consults the same gate (see isExpectedError).
  if (isExpectedError(error)) return
  if (context) {
    Sentry.withScope((scope) => {
      Object.entries(context).forEach(([key, value]) => {
        scope.setContext(key, { value })
      })
      Sentry.captureException(error)
    })
  } else {
    Sentry.captureException(error)
  }
}

// Re-export Sentry for direct access when needed
export { Sentry }
